'use client'

import { useCallback, useMemo, useRef, useState } from 'react'

import { LoaderCircle, LocateFixed, MapPin, Search } from 'lucide-react'

import { cn } from '@/lib/utils'
import { usePlacesAutocomplete, type PlacePrediction } from '@/hooks/use-places-autocomplete'

import { useLocator } from './locator-provider'

interface PlaceDetailsResponse {
    lat: number
    lng: number
    name: string | null
    formattedAddress: string | null
}

interface LocatorSearchProps {
    /** Distinguishes the hero and footer-recap instances for ids/a11y. */
    formId: string
    /** The hero shows the label + geolocate row; the footer recap is compact. */
    variant: 'hero' | 'recap'
}

function scrollToScreens() {
    document.getElementById('screens')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

/**
 * Keep only US/Canada suggestions. Every real 70mm IMAX screen is in North
 * America, so a US ZIP that also matches postal codes abroad (e.g. "90028"
 * in Chile, "900 28" in Slovakia) must not surface those foreign places.
 */
function isNorthAmerican(prediction: PlacePrediction): boolean {
    return /,\s*(USA|Canada)$/.test(prediction.description.trim())
}

/**
 * The locator search box: Wanderu places autocomplete → place details →
 * distance-sorted theater list. "Use my current location" skips the geocode
 * and uses browser geolocation directly.
 */
export const LocatorSearch = ({ formId, variant }: LocatorSearchProps) => {
    const { setOrigin } = useLocator()
    const [input, setInput] = useState('')
    const [open, setOpen] = useState(false)
    const [focusedIndex, setFocusedIndex] = useState(-1)
    const [resolving, setResolving] = useState(false)
    const [locating, setLocating] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const { predictions: rawPredictions } = usePlacesAutocomplete(input)
    const predictions = useMemo(() => rawPredictions.filter(isNorthAmerican), [rawPredictions])
    const showList = open && input.trim().length >= 2 && predictions.length > 0
    const listboxId = `${formId}-locator-options`

    const selectPrediction = useCallback(
        async (prediction: PlacePrediction) => {
            setOpen(false)
            setFocusedIndex(-1)
            setInput(prediction.description)
            setError(null)
            setResolving(true)
            try {
                const response = await fetch(
                    `/api/nexus/places/details?placeId=${encodeURIComponent(prediction.placeId)}`,
                )
                if (!response.ok) {
                    throw new Error(`place details ${response.status}`)
                }
                const details = (await response.json()) as PlaceDetailsResponse
                setOrigin({ lat: details.lat, lng: details.lng, label: prediction.mainText })
                scrollToScreens()
            } catch {
                setError("Couldn't place that spot. Try a city or ZIP.")
            } finally {
                setResolving(false)
            }
        },
        [setOrigin],
    )

    const find = () => {
        if (predictions.length > 0) {
            void selectPrediction(predictions[0])
        } else if (input.trim().length === 0) {
            inputRef.current?.focus()
        } else {
            setError("Couldn't place that spot. Try a city or ZIP.")
        }
    }

    const useMyLocation = () => {
        if (locating) {
            return
        }
        setError(null)
        if (!navigator.geolocation) {
            setError("Your browser doesn't share location. Type a city instead.")
            return
        }
        setLocating(true)
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLocating(false)
                setOrigin({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    label: 'your location',
                })
                scrollToScreens()
            },
            () => {
                setLocating(false)
                setError("Couldn't get your location. Type a city instead.")
            },
            { maximumAge: 300000, timeout: 12000 },
        )
    }

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown' && showList) {
            e.preventDefault()
            setFocusedIndex((i) => Math.min(i + 1, predictions.length - 1))
        } else if (e.key === 'ArrowUp' && showList) {
            e.preventDefault()
            setFocusedIndex((i) => Math.max(i - 1, 0))
        } else if (e.key === 'Enter') {
            e.preventDefault()
            if (showList && focusedIndex >= 0) {
                void selectPrediction(predictions[focusedIndex])
            } else {
                find()
            }
        } else if (e.key === 'Escape') {
            setOpen(false)
            setFocusedIndex(-1)
        }
    }

    return (
        <div>
            {variant === 'hero' && (
                <label
                    htmlFor={`${formId}-locator-input`}
                    className="mb-[10px] block text-sm font-bold text-[var(--imax-fg)]"
                >
                    Where are you starting from?
                </label>
            )}
            <div className="flex flex-wrap gap-[10px]">
                <div className="relative flex flex-1 basis-[240px] items-center">
                    <MapPin
                        className="pointer-events-none absolute left-3 h-5 w-5 text-[var(--imax-smoke-600)]"
                        aria-hidden="true"
                    />
                    <input
                        ref={inputRef}
                        id={`${formId}-locator-input`}
                        type="text"
                        role="combobox"
                        aria-expanded={showList}
                        aria-controls={listboxId}
                        aria-autocomplete="list"
                        aria-label={
                            variant === 'recap' ? 'Where are you starting from?' : undefined
                        }
                        placeholder="Enter an address, city, or ZIP"
                        value={input}
                        onChange={(e) => {
                            setInput(e.target.value)
                            setOpen(true)
                            setFocusedIndex(-1)
                            setError(null)
                        }}
                        onKeyDown={onKeyDown}
                        onBlur={() => {
                            // Delay so option mousedown wins over blur-close.
                            setTimeout(() => setOpen(false), 150)
                        }}
                        className="w-full rounded-[3px] border border-[var(--imax-border-dark)] py-3 pr-3 pl-10 font-sans text-base text-[var(--imax-fg)] outline-none placeholder:text-[var(--imax-smoke-600)] focus:border-[var(--imax-blu-700)] focus:shadow-[var(--imax-ring)]"
                    />
                    {showList && (
                        <ul
                            id={listboxId}
                            role="listbox"
                            aria-label="Location suggestions"
                            className="absolute top-full right-0 left-0 z-30 mt-1 max-h-[280px] overflow-y-auto rounded-[3px] border border-[var(--imax-border)] bg-white py-1 shadow-[var(--imax-shadow-3)]"
                        >
                            {predictions.map((prediction, i) => (
                                <li
                                    key={prediction.placeId}
                                    role="option"
                                    aria-selected={i === focusedIndex}
                                >
                                    <button
                                        type="button"
                                        onMouseDown={(e) => {
                                            e.preventDefault()
                                            void selectPrediction(prediction)
                                        }}
                                        className={cn(
                                            'flex w-full items-baseline gap-2 px-3 py-2 text-left text-sm',
                                            i === focusedIndex
                                                ? 'bg-[var(--imax-blu-25)]'
                                                : 'hover:bg-[var(--imax-smoke-50)]',
                                        )}
                                    >
                                        <span className="font-semibold text-[var(--imax-fg)]">
                                            {prediction.mainText}
                                        </span>
                                        <span className="truncate text-[13px] text-[var(--imax-fg-3)]">
                                            {prediction.secondaryText}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <button
                    type="button"
                    onClick={find}
                    disabled={resolving}
                    className="inline-flex h-12 cursor-pointer items-center justify-center gap-2 rounded-[3px] border-none bg-[var(--imax-primary)] px-[22px] font-sans text-base font-bold text-white transition-colors duration-150 hover:bg-[var(--imax-primary-hover)] disabled:cursor-default disabled:opacity-70"
                >
                    {resolving ? (
                        <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
                    ) : (
                        <Search className="h-5 w-5" aria-hidden="true" />
                    )}
                    Find Theaters
                </button>
            </div>
            {variant === 'hero' && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        onClick={useMyLocation}
                        className="inline-flex cursor-pointer items-center gap-[7px] border-none bg-transparent py-1 font-sans text-sm font-semibold text-[var(--imax-blu-700)] hover:text-[var(--imax-blu-900)]"
                    >
                        {locating ? (
                            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                        ) : (
                            <LocateFixed className="h-4 w-4" aria-hidden="true" />
                        )}
                        {locating ? 'Finding you…' : 'Use my current location'}
                    </button>
                    <span className="text-[13px] text-[var(--imax-smoke-600)]">
                        · Free to search, always
                    </span>
                </div>
            )}
            {error && (
                <p role="alert" className="mt-2 text-[13px] font-semibold text-[var(--imax-red)]">
                    {error}
                </p>
            )}
        </div>
    )
}
